import { useEffect, useState, useRef, useMemo } from "react";
import NumberFlow from "@number-flow/react";
import { FaFileInvoice, FaBuilding, FaArrowUp, FaArrowDown, FaEquals, FaBox, FaSort } from "react-icons/fa";
import coffeeBean from "../../Assets/icons/coffee_bean.png";
import teaLeaf from "../../Assets/icons/tea_leaf.png";
import UnfindDataWarn from "../Utils/UnfindDataWarn";
import mascotNoData from "../../Assets/icons/okkali_mascot_nodata.png";
import MascotLoadingOverlay from "../shared/MascotLoadingOverlay";
import SharedDrawer from "../shared/Drawer";
import SearchInput from "../shared/SearchInput";
import useAuth from "../../Hooks/useAuth";
import { useLazyGetBillsByProductAndPeriodQuery } from "../../store/storageRoles/dashboardApi";
import CustomStyles from "../../CustomStyles";

// NumberFlow prop'ları modül sabiti (#233): satır içi nesneler her render'da
// yeni referans olup biçimlendiriciyi / zamanlamayı yeniden kurduruyordu.
const NUMBER_FORMAT = { useGrouping: true };
const NUMBER_FORMAT_INT = { useGrouping: true, maximumFractionDigits: 0 };
const NUMBER_TIMING = { duration: 500, easing: "ease-out" };

const defaultProducts = [
  {
    name: "Çay",
    icon: teaLeaf,
    color: CustomStyles.cay,
    bgColor: "#E8F5E9",
  },
  {
    name: "Türk Kahvesi",
    icon: coffeeBean,
    color: CustomStyles.türkKahvesi,
    bgColor: "#E8EAF6",
  },
  {
    name: "Filtre Kahve",
    icon: coffeeBean,
    color: CustomStyles.filtreKahve,
    bgColor: "#FFF3E0",
  },
  {
    name: "Espresso Bazlı",
    displayName: "Espresso",
    icon: coffeeBean,
    color: "#4E342E",
    bgColor: "#EFEBE9",
  },
];

const BASE_INCOME_PRODUCTS = ["Çay", "Türk Kahvesi", "Filtre Kahve"];

const INCOME_PERIOD_MAP = {
  today: "dailyRevenue",
  yesterday: "yesterdayRevenue",
  thisweek: "weeklyRevenue",
  thismonth: "monthlyRevenue",
  thisyear: "yearlyRevenue",
};

const SALES_QUANTITY_PERIOD_MAP = {
  today: "dailySalesQuantity",
  yesterday: "yesterdaySalesQuantity",
  thisweek: "weeklySalesQuantity",
  thismonth: "monthlySalesQuantity",
  thisyear: "yearlySalesQuantity",
};

const calculatePercentages = (consumption) => {
  if (!Array.isArray(consumption)) return [];

  const totalConsumption = consumption.reduce(
    (total, item) => total + (item.consument || 0),
    0
  );

  const dataMap = new Map(
    defaultProducts.map((product) => [
      product.name,
      { ...product, amount: 0, deviceCount: 0, firmCount: 0 },
    ])
  );

  consumption.forEach((item) => {
    if (dataMap.has(item.productName)) {
      dataMap.get(item.productName).amount = item.consument || 0;
      dataMap.get(item.productName).deviceCount = item.deviceCount || 0;
      dataMap.get(item.productName).firmCount = item.firmCount || 0;
    }
  });

  const result = Array.from(dataMap.values()).map((item) => ({
    ...item,
    label: item.displayName || item.name,
    percentage:
      totalConsumption > 0
        ? Math.round((item.amount / totalConsumption) * 100)
        : 0,
    // Makine başı ortalama — cihaz kutusunda gösterilir
    average:
      item.deviceCount && item.deviceCount > 0
        ? parseFloat((item.amount / item.deviceCount).toFixed(1))
        : 0,
    // Firma başı ortalama — firma kutusunda gösterilir
    firmAverage:
      item.firmCount && item.firmCount > 0
        ? parseFloat((item.amount / item.firmCount).toFixed(1))
        : 0,
  }));

  return result;
};

const PERIOD_LABELS = {
  today: "Bugün",
  yesterday: "Dün",
  thisweek: "Bu Hafta",
  thismonth: "Bu Ay",
  thisyear: "Bu Yıl",
};

const formatBillDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return String(dateStr);
  }
};

const formatBillTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

// Önceki döneme göre performans rozeti.
// changePercent: sayı | null (null = önceki dönem 0, mevcut > 0 → "yeni") | undefined (veri yok)
// Not: ConsumptionBreakdownCard da kullanır (named export).
export const PerformanceBadge = ({ changePercent, showText = true, compact = false, className = "" }) => {
  if (changePercent === undefined) return null;

  const isNew = changePercent === null;
  const flat = !isNew && changePercent === 0;
  const up = isNew || changePercent > 0;

  const color = flat
    ? "text-gray-400"
    : up
      ? "text-green-600"
      : "text-red-500";
  const Icon = flat ? FaEquals : up ? FaArrowUp : FaArrowDown;
  const text = isNew
    ? "yeni"
    : flat
      ? "%0"
      : compact
        ? `%${Math.abs(changePercent).toLocaleString("tr-TR")}`
        : `%${Math.abs(changePercent).toLocaleString("tr-TR")} ${up ? "daha iyi" : "daha kötü"}`;

  return (
    <span
      className={`inline-flex items-center gap-0.5 font-bold whitespace-nowrap ${color} ${className}`}
      title={`Önceki döneme göre: ${text}`}
    >
      <Icon className="w-2.5 h-2.5 shrink-0" />
      {showText && text}
    </span>
  );
};

export default function ProductConsumentCard({
  consumption = null,
  performance = null,
  warn,
  dashboardType,
  dashboardConsumentIsLoading,
  canSeeBillDetails = true,
  incomes,
  salesQuantity,
  activeConsumption,
}) {
  const { auth } = useAuth();
  const [getBillsByProductAndPeriod, { data: billsResponse, isFetching: billsLoading }] = useLazyGetBillsByProductAndPeriodQuery();
  // Ürün ismi yanındaki performans rozetleri — socket'ten gelir (tüm periyotlar),
  // aktif gün filtresine göre yerelde seçilir
  const performanceByProduct = performance?.[activeConsumption] || {};
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProduct, setDrawerProduct] = useState(null); // { productName, productLabel }
  const [consumptionData, setConsumptionData] = useState([]);
  // Satış tablosu sıralaması — sütun başlığına tıklayınca artan/azalan
  const [billSort, setBillSort] = useState({ key: "createdInfo", dir: "desc" });
  // Satış tablosu arama — ayrı alanlar: firma / fatura no / bayser no
  const [billSearch, setBillSearch] = useState({ firmName: "", billNo: "", bayserNo: "" });
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    if (drawerOpen && drawerProduct && auth?.accessToken) {
      setBillSearch({ firmName: "", billNo: "", bayserNo: "" });
      getBillsByProductAndPeriod({
        token: auth.accessToken,
        productName: drawerProduct.productName,
        period: activeConsumption || "thisweek",
      });
    }
  }, [drawerOpen, drawerProduct?.productName, activeConsumption, auth?.accessToken]);

  useEffect(() => {
    if (!hasLoadedOnce.current) {
      setConsumptionData([]);
    }
  }, [dashboardType]);

  useEffect(() => {
    if (consumption == null) return; // null/undefined ise henüz veri gelmemiş
    const data = calculatePercentages(consumption);
    setConsumptionData(data);
    hasLoadedOnce.current = true;
  }, [consumption]);

  // Fatura satış adedi (quota toplamı) – aktif filtreye göre
  const salesQuantityByProduct = useMemo(() => {
    if (!salesQuantity) return {};
    const periodKey = SALES_QUANTITY_PERIOD_MAP[activeConsumption] || "yearlySalesQuantity";
    const quantityData = salesQuantity[periodKey] || {};

    const result = {};
    let espressoTotal = 0;
    Object.entries(quantityData).forEach(([product, qty]) => {
      if (BASE_INCOME_PRODUCTS.includes(product)) {
        result[product] = qty || 0;
      } else {
        espressoTotal += qty || 0;
      }
    });
    result["Espresso Bazlı"] = espressoTotal;

    return result;
  }, [salesQuantity, activeConsumption]);

  // Satış tablosu — fatura satırlarını düzleştir + ara + seçili sütuna göre sırala
  const sortedBillRows = useMemo(() => {
    const qFirm = billSearch.firmName.trim().toLowerCase();
    const qBill = billSearch.billNo.trim().toLowerCase();
    const qBayser = billSearch.bayserNo.trim().toLowerCase();
    const rows = [];
    (billsResponse?.bills || []).forEach((bill) => {
      // Arama: firma ismi / fatura no / bayser no (ayrı alanlar, AND)
      if (
        !`${bill.firmName || ""}`.toLowerCase().includes(qFirm) ||
        !`${bill.billNo || ""}`.toLowerCase().includes(qBill) ||
        !`${bill.bayserNo || ""}`.toLowerCase().includes(qBayser)
      ) {
        return;
      }
      (bill.items || []).forEach((item) => {
        rows.push({
          billNo: bill.billNo || "—",
          firmName: bill.firmName || "—",
          bayserNo: bill.bayserNo || null,
          createdInfo: bill.createdInfo,
          productName: item.productName || item.productTypeName || "—",
          quota: Number(item.quota ?? 0),
          income: Number(item.income ?? 0),
        });
      });
    });
    const { key, dir } = billSort;
    const mul = dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let av = a[key];
      let bv = b[key];
      if (key === "createdInfo") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (typeof av === "string" || typeof bv === "string") {
        return String(av).localeCompare(String(bv), "tr") * mul;
      }
      return (av - bv) * mul;
    });
    return rows;
  }, [billsResponse, billSort, billSearch]);

  const toggleBillSort = (key) =>
    setBillSort((p) =>
      p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );

  // Skeleton: ilk yükleme veya scope/değişim sonrası loading (Bayim/Bayilerim/Müşterilerim/Tüm Müşteriler)
  const showSkeleton = !hasLoadedOnce.current || dashboardConsumentIsLoading;

  if (showSkeleton) {
    return (
      <div className="relative border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm h-full">
        <div className="grid grid-cols-2 grid-rows-2 gap-0 h-full opacity-40">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={idx}
              className="relative flex flex-col items-center justify-center gap-1.5 px-3 py-2 sm:px-4 sm:py-3"
              style={{
                borderRight: idx % 2 === 0 ? "1px solid #E5E7EB" : "none",
                borderBottom: idx < 2 ? "1px solid #E5E7EB" : "none",
              }}
            >
              <div className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-14 h-14 sm:w-20 sm:h-20 bg-gray-200 rounded-full opacity-20 animate-pulse" />
              <div className="h-8 sm:h-9 w-16 sm:w-20 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-4 sm:h-5 w-20 sm:w-24 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 sm:h-4 w-16 sm:w-20 bg-gray-200 rounded animate-pulse" />
              <div className="flex items-center gap-2">
                <div className="h-2.5 sm:h-3 w-12 sm:w-14 bg-gray-200 rounded animate-pulse" />
                <div className="h-2.5 sm:h-3 w-12 sm:w-14 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        <MascotLoadingOverlay message="Tüketim verileri yükleniyor..." />
      </div>
    );
  }

  if (consumption == null && !hasLoadedOnce.current) {
    return <UnfindDataWarn Data={warn} />;
  }

  return (
    <div className="relative border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm h-full">
      {/* 2x2 Grid */}
      <div className="grid grid-cols-2 grid-rows-2 gap-0 h-full">
        {consumptionData.map((product, index) => (
            <div
              key={product.name}
              className="relative flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-3 py-2 sm:px-4 sm:py-3"
              style={{
                borderRight: index % 2 === 0 ? "1px solid #E5E7EB" : "none",
                borderBottom: index < 2 ? "1px solid #E5E7EB" : "none",
              }}
            >
              <img
                src={product.icon}
                alt=""
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-14 h-14 sm:w-20 sm:h-20 opacity-10 pointer-events-none select-none"
              />
              <NumberFlow
                value={product.amount}
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold"
                style={{ color: product.color }}
                format={NUMBER_FORMAT}
                transformTiming={NUMBER_TIMING}
                spinTiming={NUMBER_TIMING}
              />
              <div className="flex items-center justify-center gap-1.5 flex-wrap">
                <span className="text-sm sm:text-base text-gray-700 font-semibold">
                  {product.label}
                </span>
                <PerformanceBadge
                  changePercent={performanceByProduct[product.name]?.changePercent}
                  className="text-[9px] sm:text-[11px]"
                />
              </div>
              {canSeeBillDetails && (
                salesQuantity ? (
                  <button
                    type="button"
                    disabled={!(salesQuantityByProduct[product.name] > 0)}
                    onClick={() => {
                      setDrawerProduct({ productName: product.name, productLabel: product.label });
                      setDrawerOpen(true);
                    }}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-xs sm:text-sm font-bold transition-colors ${
                      salesQuantityByProduct[product.name] > 0
                        ? "border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
                        : "border-gray-200 text-gray-400 cursor-default"
                    }`}
                    aria-label={`${product.label} satış faturaları`}
                  >
                    <FaFileInvoice size={12} className="shrink-0" />
                    <span>
                      Satış: <NumberFlow
                        value={salesQuantityByProduct[product.name] ?? 0}
                        format={NUMBER_FORMAT_INT}
                        transformTiming={NUMBER_TIMING}
                        spinTiming={NUMBER_TIMING}
                      /> adet
                    </span>
                  </button>
                ) : (
                  // Fatura verisi henüz gelmediyse buton kaybolmasın — placeholder
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-gray-200 text-gray-400 text-xs sm:text-sm font-bold">
                    <FaFileInvoice size={12} className="shrink-0" />
                    <span>Satış:</span>
                    <span className="inline-block h-3 w-10 bg-gray-200 rounded animate-pulse" />
                  </span>
                )
              )}
            </div>
        ))}
      </div>

      <SharedDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width="xl"
        headerGradient
        title={`${drawerProduct?.productLabel ?? "Ürün"} – Satış Faturaları`}
        subtitle={PERIOD_LABELS[activeConsumption] ?? activeConsumption}
        titleIcon={<FaFileInvoice className="w-5 h-5" />}
      >
        {/* Sabit özet bandı — toplam gelir / kota / fatura */}
        {!billsLoading && billsResponse?.bills?.length > 0 && billsResponse.summary && (
          <div className="shrink-0 px-4 pt-3 pb-2.5 bg-gray-50 border-b border-gray-200 grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm px-3 py-2">
              <p className="text-[11px] text-gray-500">Toplam gelir</p>
              <p className="text-base sm:text-lg font-extrabold text-blue-600 break-all">
                {Number(billsResponse.summary.totalIncome ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
              </p>
            </div>
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm px-3 py-2">
              <p className="text-[11px] text-gray-500">Toplam kota</p>
              <p className="text-base sm:text-lg font-extrabold text-gray-900">
                {Number(billsResponse.summary.totalQuota ?? 0).toLocaleString("tr-TR")}
              </p>
            </div>
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm px-3 py-2">
              <p className="text-[11px] text-gray-500">Fatura</p>
              <p className="text-base sm:text-lg font-extrabold text-gray-900">
                {Number(billsResponse.summary.billCount ?? 0).toLocaleString("tr-TR")}
              </p>
            </div>
          </div>
        )}
        {/* Arama — ayrı alanlar: firma / fatura no / bayser no */}
        {!billsLoading && billsResponse?.bills?.length > 0 && (
          <div className="shrink-0 px-4 py-2.5 bg-white border-b border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <SearchInput
              placeholder="Firma İsmi"
              value={billSearch.firmName}
              onChange={(e) => setBillSearch((p) => ({ ...p, firmName: e.target.value }))}
            />
            <SearchInput
              placeholder="Fatura No"
              value={billSearch.billNo}
              onChange={(e) => setBillSearch((p) => ({ ...p, billNo: e.target.value }))}
            />
            <SearchInput
              placeholder="Bayser No"
              value={billSearch.bayserNo}
              onChange={(e) => setBillSearch((p) => ({ ...p, bayserNo: e.target.value }))}
            />
          </div>
        )}
        <SharedDrawer.Body className="bg-gray-50/80">
          {billsLoading ? (
            <div className="bg-white">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-3 border-b border-gray-100 last:border-0">
                  <div className="h-3.5 w-20 bg-gray-200 rounded animate-pulse" />
                  <div className="h-3.5 flex-1 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3.5 w-12 bg-gray-200 rounded animate-pulse" />
                  <div className="h-3.5 w-16 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : !billsResponse?.bills?.length ? (
            <div className="bg-white p-8 text-center select-none">
              <img src={mascotNoData} alt="Fatura bulunamadı" className="w-24 h-24 sm:w-32 sm:h-32 object-contain mx-auto mb-3 drop-shadow-lg" draggable={false} />
              <p className="text-base font-bold text-gray-700">Bu dönemde fatura bulunamadı.</p>
            </div>
          ) : sortedBillRows.length === 0 ? (
            <div className="bg-white p-8 text-center select-none">
              <p className="text-sm font-medium text-gray-500">Aramaya uygun fatura bulunamadı.</p>
            </div>
          ) : (
            <div className="bg-white">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 shadow-sm">
                    {[
                      { key: "billNo", label: "Fatura No", align: "left" },
                      { key: "firmName", label: "Firma", align: "left" },
                      { key: "productName", label: "Ürün", align: "left" },
                      { key: "quota", label: "Kota", align: "right" },
                      { key: "income", label: "Gelir", align: "right" },
                      { key: "createdInfo", label: "Tarih", align: "left" },
                    ].map((col) => (
                      <th key={col.key} className={`px-3 py-2.5 bg-gray-50 ${col.align === "right" ? "text-right" : ""}`}>
                        <button
                          type="button"
                          onClick={() => toggleBillSort(col.key)}
                          className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-700 transition-colors ${col.align === "right" ? "flex-row-reverse" : ""} ${billSort.key === col.key ? "text-gray-800" : ""}`}
                        >
                          {col.label}
                          {billSort.key === col.key
                            ? (billSort.dir === "asc"
                                ? <FaArrowUp className="w-2.5 h-2.5 text-blue-600" />
                                : <FaArrowDown className="w-2.5 h-2.5 text-blue-600" />)
                            : <FaSort className="w-2.5 h-2.5 text-gray-300" />}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedBillRows.map((row, i) => (
                    <tr
                      key={`${row.billNo}-${i}`}
                      className={`align-top border-t border-gray-100 ${i % 2 === 1 ? "bg-gray-50/70" : "bg-white"} hover:bg-indigo-50/40 transition-colors`}
                    >
                      <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">
                        {row.billNo}
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        <span className="flex items-start gap-1.5">
                          <FaBuilding className="w-3 h-3 text-indigo-400 shrink-0 mt-0.5" />
                          <span className="break-words">
                            {row.firmName}
                            {row.bayserNo && (
                              <span className="ml-1 text-gray-400 font-normal">#{row.bayserNo}</span>
                            )}
                          </span>
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-violet-600">{row.productName}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-600 whitespace-nowrap">
                        <span className="inline-flex items-center justify-end gap-1">
                          {row.quota.toLocaleString("tr-TR")}
                          <FaBox className="w-2.5 h-2.5 text-emerald-400 shrink-0" />
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-blue-600 whitespace-nowrap">
                        {row.income.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                      </td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        <div className="flex flex-col leading-tight">
                          <span>{formatBillDate(row.createdInfo)}</span>
                          {formatBillTime(row.createdInfo) && (
                            <span className="text-[11px] text-gray-400">{formatBillTime(row.createdInfo)}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SharedDrawer.Body>
      </SharedDrawer>
    </div>
  );
}
